import { isPosthogEnabled, withPosthog } from '@/lib/posthog';
import { isLegacyRoute, isRouteOrDescendant } from '@/routes/routePatterns';
import { ERatingTab, ERoutes } from '@/types/routes';
import type { TLogoutReason } from '@/providers/AuthProvider/NewAuthProvider.types';
import {
  TAuthEntryType,
  TAuthMechanism,
  getCurrentAuthDay,
  readDailyAuthStateMarker,
  shouldCaptureDailyAuthState,
  writeDailyAuthStateMarker,
} from '@/helpers/dailyAuthState';

type TRefreshTrigger = 'interval';

function formatSessionDuration(sessionDurationSec?: number) {
  if (sessionDurationSec === undefined) return null;

  const totalSeconds = Math.max(0, Math.floor(sessionDurationSec));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts: string[] = [];

  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);

  return parts.join(' ');
}

function getPageName(pathname: string) {
  if (pathname === '/dashboard') {
    return 'Dashboard';
  }
  if (pathname === '/dashboard/settings') {
    return 'Dashboard - settings';
  }
  if (/^\/entity\/([a-zA-Z0-9_]+)$/.test(pathname)) {
    return 'Entity details';
  }
  if (pathname === '/risk-simulator') {
    return 'Risk Simulator';
  }
  if (pathname === '/risk-simulator/portfolio') {
    return 'Risk Simulator - Portfolio';
  }
  if (pathname === '/stablecoins') {
    return 'Stablecoins';
  }
  if (/^\/stablecoins\/([a-zA-Z0-9_]+)$/.test(pathname)) {
    return 'Stablecoins - details';
  }
  if (isRouteOrDescendant(pathname, ERoutes.DOCUMENTATION)) {
    return 'Documentation';
  }
  if (pathname === '/alerting') {
    return 'Alerting';
  }
  if (isRouteOrDescendant(pathname, ERoutes.ALERTING)) {
    return 'Alerting - details';
  }
  if (pathname === '/settings') {
    return 'Settings';
  }
  if (pathname === '/login') {
    return 'Login';
  }
  if (pathname === '/forgot-password') {
    return 'Reset Password';
  }
  if (pathname === '/reset-password') {
    return 'Set New Password';
  }
  if (isRouteOrDescendant(pathname, ERoutes.ONBOARDING)) {
    return 'Onboarding';
  }

  return pathname;
}

function getTabName(pathname: string, search: string) {
  if (pathname === '/ratings') {
    if (search === ERatingTab.EXCHANGES) {
      return 'Exchanges Tab';
    }

    if (search === ERatingTab.CUSTODIANS) {
      return 'Custodians Tab';
    }
  }

  return search;
}

const resetTracking = (logoutReason: TLogoutReason = 'manual', sessionDurationSec?: number) => {
  const sessionDurationHumanReadable = formatSessionDuration(sessionDurationSec);

  withPosthog(posthog => {
    posthog.capture('auth_logout', {
      time: new Date().toISOString(),
      reason: logoutReason,
      sessionDurationSec: sessionDurationSec ?? null,
      sessionDurationHumanReadable,
    });

    // legacy data extended with Reason SessionDurationSec and SessionDurationHumanReadable keys - 24.04.2026
    posthog.capture('User Logout', {
      Time: new Date().toISOString(),
      Reason: logoutReason,
      SessionDurationSec: sessionDurationSec ?? null,
      SessionDurationHumanReadable: sessionDurationHumanReadable,
    });
    posthog.reset();
  });
};

const identifyUser = <T extends { id: string | number }>(user: T) => {
  withPosthog(posthog => {
    const { id, ...rest } = user;
    posthog.identify(String(id));
    posthog.setPersonProperties(rest);
  });
};

const trackEvent = (eventName: string, eventProperties: Record<string, unknown> = {}) => {
  withPosthog(posthog => {
    posthog.capture(eventName, eventProperties);
  });
};

const trackLogin = (userEmail: string, userPermissions: string) => {
  withPosthog(posthog => {
    posthog.capture('auth_login_success', {
      time: new Date().toISOString(),
      userEmail: userEmail,
      permissions: userPermissions,
    });

    posthog.capture('User Login Info', {
      Time: new Date().toISOString(),
      UserEmail: userEmail,
      Permissions: userPermissions,
    });
  });
};

const trackRefresh = (
  status: 'success' | 'failed',
  trigger: TRefreshTrigger,
  errorMessage?: string,
) => {
  withPosthog(posthog => {
    posthog.capture('auth_refresh', {
      time: new Date().toISOString(),
      status,
      trigger,
      ...(errorMessage ? { errorMessage } : null),
    });
  });
};

// the root and the legacy paths only redirect, and the page they land on reports itself
const isRedirectOnlyPath = (pathname: string) => pathname === '/' || isLegacyRoute(pathname);

const trackPageView = (pathname: string, search: string) => {
  if (isRedirectOnlyPath(pathname)) return;

  const pageName = getPageName(pathname);
  const tabName = getTabName(pathname, search);

  withPosthog(posthog => {
    posthog.capture(`Page Viewed: ${pageName}`, {
      page: pathname,
      ...(tabName ? { tab: tabName } : null),
    });
    posthog.capture('$pageview', {
      $current_url: pathname,
      ...(tabName ? { $current_search: tabName } : null),
    });
  });
};

const trackFilterUsage = (filterType: string, filterValue: string | string[]) => {
  withPosthog(posthog => {
    posthog.capture(`Filter Applied: ${filterType}`, {
      'Filter Type': filterType,
      'Filter Value': filterValue,
    });
  });
};

const captureDailyAuthState = (
  userId: string,
  authEntryType: TAuthEntryType,
  authMechanism: TAuthMechanism,
) => {
  if (!isPosthogEnabled) return;

  const authDay = getCurrentAuthDay();
  const existingMarker = readDailyAuthStateMarker(userId, authDay);

  if (!shouldCaptureDailyAuthState(existingMarker, authEntryType)) return;

  withPosthog(posthog => {
    posthog.capture('daily_auth_state', {
      authEntryType: authEntryType,
      authMechanism: authMechanism,
      authDay: authDay,
    });
  });
  writeDailyAuthStateMarker(userId, authDay, authEntryType);
};

// the functions close over nothing but this module, so the hook hands back one constant object -
// a fresh identity on every render re-runs every effect and useCallback that depends on it
const TRACKING = {
  trackLogin,
  trackRefresh,
  trackPageView,
  trackFilterUsage,
  identifyUser,
  resetTracking,
  trackEvent,
  captureDailyAuthState,
};

export const useTracking = () => TRACKING;
